import * as LodashFP from 'lodash/fp'

import { IAppError, isAppError } from '../interfaces/base'
import {
  FormBlueprint,
  FieldValidation,
  ValidationMessage,
} from '../interfaces/components/dynamic-form'

import { convertPath, arrayPathFlobulator } from './object'
import { enumerateBlueprintPaths } from '../lib/dynamic-form/helpers'
import { IStringTMap } from '../types/map'
import { translateWithUserLocale } from './i18n'
import { removeLastElementFromPathString } from './string'

const isProduction = () => process.env.NODE_ENV === 'production'

const defaultAppError: () => IAppError = () => ({
  title: translateWithUserLocale('whoops') + '!',
  message: translateWithUserLocale('error.something_went_wrong_apologetic'),
  statusCode: '🙊',
})

export const getApiErrorMessage: (
  response: any,
  fallback?: string,
) => string = (
  response,
  fallback = translateWithUserLocale('error.something_went_wrong_apologetic'),
) => {
  const message = LodashFP.getOr(null, 'data.message', response)
  const errors = LodashFP.flattenDeep(
    LodashFP.getOr([], 'data.errors', response),
  ) as any[]

  const result = LodashFP.cond([
    [
      () => {
        if (LodashFP.isArray(errors) && errors.length > 0) {
          const msg = errors.find(
            (e: any) => LodashFP.isString(e) && (e as string).length > 0,
          ) as string | undefined

          return msg !== undefined
        }

        return false
      },
      () => {
        return errors.find(
          (e: any) => LodashFP.isString(e) && (e as string).length > 0,
        ) as string
      },
    ],
    [
      () => LodashFP.isString(message) && message.length > 0,
      () => message as string,
    ],
    [() => true, () => fallback],
  ])('')

  return result
}

export const appErrorFromAnything: (
  error: any,
  fallback?: string,
) => IAppError = (error, fallback = defaultAppError().message) => {
  try {
    if (isAppError(error)) {
      return error
    }

    if (LodashFP.isString(error)) {
      return {
        title: defaultAppError().title,
        message: error,
        statusCode: defaultAppError().statusCode,
      }
    }

    if ('response' in error && error.response != null) {
      return {
        title: error.response.statusText || defaultAppError().title,
        message: getApiErrorMessage(error.response, fallback),
        statusCode: error.response.status || defaultAppError().statusCode,
      }
    }

    return {
      title: defaultAppError().title,
      message:
        !isProduction() &&
        LodashFP.isString(error.message) &&
        error.message.length > 0
          ? error.message
          : fallback,
      statusCode: defaultAppError().statusCode,
    }
  } catch (e) {
    return {
      title: defaultAppError().title,
      message: fallback,
      statusCode: defaultAppError().statusCode,
    }
  }
}

export const getValidationMessages = (
  data: any[] = [],
  blueprint: FormBlueprint,
): IStringTMap<FieldValidation> => {
  const messages: IStringTMap<FieldValidation> = {}

  for (const msg of data || []) {
    const fieldArray = msg.field.split('.')
    const fieldWithoutFirst = fieldArray.slice(1).join('.')

    const tranformed = convertPath(fieldWithoutFirst)
    const fields = enumerateBlueprintPaths(blueprint)

    let field = tranformed

    while (!fields.includes(arrayPathFlobulator(field)) && field !== '') {
      field = removeLastElementFromPathString(field)
    }

    if (field === '') {
      continue
    }

    const status = msg.severity === 'ERROR' ? 'error' : 'warning'
    const message = {
      message: `${msg.message}`,
      status,
    } as ValidationMessage

    if (field in messages) {
      messages[field] = LodashFP.sortBy(
        (msg) => msg.status,
        [...messages[field], message],
      )
    } else {
      messages[field] = [message]
    }
  }

  return messages
}
